import type { AssetSnapshotChartData } from "@repo/share-log-interfaces";
import { z } from "zod";
import type { HttpClient } from "../http";
import type { DataAndMessageResponse, MessageResponse } from "../types";

// Types
export interface AssetResponse {
  id: number;
  name: string;
  amount: number;
  sortOrder: number;
  userId: number;
  workspaceId: number;
  createdAt: string;
  updatedAt: string;
  user: { id: number; nickname: string | null; avatarUrl: string | null };
}

export interface UserAssetGroup {
  userId: number;
  nickname: string;
  avatarUrl: string | null;
  assets: AssetResponse[];
  total: number;
}

export interface AssetListResponse {
  users: UserAssetGroup[];
  workspaceTotal: number;
}

export interface CreateAssetDto {
  name: string;
  amount: number;
}

export interface UpdateAssetDto {
  name?: string;
  amount?: number;
}

export interface AssetSnapshotChartQuery {
  startDate?: string;
  endDate?: string;
}

// Schemas
const AssetResponseSchema: z.ZodType<AssetResponse> = z.object({
  id: z.number(),
  name: z.string(),
  amount: z.number(),
  sortOrder: z.number(),
  userId: z.number(),
  workspaceId: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  user: z.object({
    id: z.number(),
    nickname: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  }),
});

const UserAssetGroupSchema: z.ZodType<UserAssetGroup> = z.object({
  userId: z.number(),
  nickname: z.string(),
  avatarUrl: z.string().nullable(),
  assets: z.array(AssetResponseSchema),
  total: z.number(),
});

const AssetListResponseSchema: z.ZodType<AssetListResponse> = z.object({
  users: z.array(UserAssetGroupSchema),
  workspaceTotal: z.number(),
});

const AssetSnapshotChartPointSchema = z.object({
  date: z.string(),
  amount: z.number(),
});

const AssetSnapshotChartDataSchema: z.ZodType<AssetSnapshotChartData> =
  z.object({
    users: z.array(
      z.object({
        userId: z.number(),
        nickname: z.string(),
        data: z.array(AssetSnapshotChartPointSchema),
      }),
    ),
    total: z.array(AssetSnapshotChartPointSchema),
  });

// Response Schemas
const CreateAssetResponseSchema: z.ZodType<
  DataAndMessageResponse<AssetResponse>
> = z.object({
  data: AssetResponseSchema,
  message: z.string(),
});

const GetAssetsResponseSchema: z.ZodType<
  DataAndMessageResponse<AssetListResponse>
> = z.object({
  data: AssetListResponseSchema,
  message: z.string(),
});

const UpdateAssetResponseSchema: z.ZodType<
  DataAndMessageResponse<AssetResponse>
> = z.object({
  data: AssetResponseSchema,
  message: z.string(),
});

const DeleteAssetResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const ReorderAssetsResponseSchema: z.ZodType<MessageResponse> = z.object({
  message: z.string(),
});

const GetSnapshotChartResponseSchema: z.ZodType<
  DataAndMessageResponse<AssetSnapshotChartData>
> = z.object({
  data: AssetSnapshotChartDataSchema,
  message: z.string(),
});

export function createAssetModule(httpClient: HttpClient) {
  return {
    create: async (workspaceId: number, data: CreateAssetDto) => {
      const response = await httpClient.post<typeof CreateAssetResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/assets`,
        {
          data,
          schema: CreateAssetResponseSchema,
        },
      );
      return response.data;
    },

    list: async (workspaceId: number) => {
      const response = await httpClient.get<typeof GetAssetsResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/assets`,
        {
          schema: GetAssetsResponseSchema,
        },
      );
      return response.data;
    },

    update: async (
      workspaceId: number,
      assetId: number,
      data: UpdateAssetDto,
    ) => {
      const response = await httpClient.patch<typeof UpdateAssetResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/assets/${assetId}`,
        {
          data,
          schema: UpdateAssetResponseSchema,
        },
      );
      return response.data;
    },

    remove: async (workspaceId: number, assetId: number) => {
      const response = await httpClient.delete<typeof DeleteAssetResponseSchema>(
        `/api/v1/workspaces/${workspaceId}/assets/${assetId}`,
        {
          schema: DeleteAssetResponseSchema,
        },
      );
      return response;
    },

    reorder: async (workspaceId: number, assetIds: number[]) => {
      const response = await httpClient.put<
        typeof ReorderAssetsResponseSchema
      >(`/api/v1/workspaces/${workspaceId}/assets/reorder`, {
        data: { assetIds },
        schema: ReorderAssetsResponseSchema,
      });
      return response;
    },

    getSnapshotChart: async (
      workspaceId: number,
      query?: AssetSnapshotChartQuery,
    ) => {
      const params = new URLSearchParams();
      if (query?.startDate) params.set("startDate", query.startDate);
      if (query?.endDate) params.set("endDate", query.endDate);

      const queryString = params.toString();
      const url = `/api/v1/workspaces/${workspaceId}/assets/snapshots/chart${queryString ? `?${queryString}` : ""}`;

      const response = await httpClient.get<
        typeof GetSnapshotChartResponseSchema
      >(url, {
        schema: GetSnapshotChartResponseSchema,
      });
      return response.data;
    },
  };
}
